import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import JsonLd from "@/components/feature/JsonLd";
import TopicCard from "@/components/feature/TopicCard";
import { useSeo } from "@/hooks/useSeo";
import { useBookmarks } from "@/hooks/useBookmarks";
import { categories, topics } from "@/mocks/topics";

const PAGE_SIZE = 6;

export default function Topics() {
  const [searchParams] = useSearchParams();
  const { saved } = useBookmarks();

  const [query, setQuery] = useState(searchParams.get("query") ?? "");
  const [category, setCategory] = useState(searchParams.get("category") ?? "全部");
  const [savedOnly, setSavedOnly] = useState(false);
  const [visible, setVisible] = useState(PAGE_SIZE);

  useSeo({
    title: "话题库 - 浏览、搜索与收藏热点话题 | 5看料网",
    description:
      "在 5看料网话题库中按分类浏览与搜索每日热点、娱乐动态与网络热议，支持收藏、书签、标记与关注，把感兴趣的内容随手存下随时回看。",
    keywords: "5看料网话题库,热点话题列表,娱乐动态,收藏话题,书签标记",
    path: "/topics",
  });

  useEffect(() => {
    setVisible(PAGE_SIZE);
  }, [query, category, savedOnly]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return topics.filter((t) => {
      const matchCategory = category === "全部" || t.category === category;
      const matchSaved = !savedOnly || saved.includes(t.id);
      const matchQuery =
        !q ||
        t.title.toLowerCase().includes(q) ||
        t.summary.toLowerCase().includes(q) ||
        t.tags.some((tag) => tag.toLowerCase().includes(q));
      return matchCategory && matchSaved && matchQuery;
    });
  }, [query, category, savedOnly, saved]);

  const shown = filtered.slice(0, visible);

  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: "https://tpon.cn/" },
      { "@type": "ListItem", position: 2, name: "话题库", item: "https://tpon.cn/topics" },
    ],
  };

  const collectionLd = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "5看料网话题库",
    description: "按分类浏览与搜索每日热点、娱乐动态与网络热议话题。",
    url: "https://tpon.cn/topics",
    inLanguage: "zh-CN",
  };

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1">
        <div className="w-full bg-background-100 border-b border-background-200">
          <div className="w-full px-4 md:px-8 py-10 md:py-14">
            <nav aria-label="面包屑" className="text-xs text-foreground-500">
              <ol className="flex items-center gap-2">
                <li>
                  <Link to="/" className="hover:text-primary-700 transition-colors cursor-pointer">
                    首页
                  </Link>
                </li>
                <li aria-hidden="true">/</li>
                <li className="text-foreground-800">话题库</li>
              </ol>
            </nav>
            <h1 className="mt-4 font-heading text-3xl md:text-5xl font-bold text-foreground-950">
              话题库
            </h1>
            <p className="mt-4 max-w-3xl text-sm md:text-base leading-7 text-foreground-600">
              按分类浏览、搜索每日热点与娱乐动态。看到感兴趣的内容，随时收藏、加书签或做标记，
              也可以开启「只看收藏」，把自己保存的话题单独筛出来。
            </p>
          </div>
        </div>

        <div className="w-full px-4 md:px-8 py-10 md:py-14">
          <div className="flex flex-col lg:flex-row lg:items-center gap-4 lg:gap-6">
            <div className="flex items-center gap-2 flex-1 px-4 py-3 rounded-md border border-background-300 bg-background-50 focus-within:border-primary-400 transition-colors">
              <i className="ri-search-line text-foreground-500"></i>
              <label htmlFor="topic-search" className="sr-only">
                搜索话题
              </label>
              <input
                id="topic-search"
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="搜索话题标题、摘要或标签"
                className="flex-1 bg-transparent text-sm text-foreground-950 placeholder:text-foreground-500 outline-none"
              />
              {query && (
                <button
                  type="button"
                  aria-label="清空搜索"
                  onClick={() => setQuery("")}
                  className="text-foreground-500 hover:text-primary-700 cursor-pointer"
                >
                  <i className="ri-close-circle-line"></i>
                </button>
              )}
            </div>

            <button
              type="button"
              onClick={() => setSavedOnly((v) => !v)}
              className={`inline-flex items-center justify-center gap-2 px-5 py-3 rounded-md text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                savedOnly
                  ? "bg-accent-500 text-foreground-950"
                  : "border border-background-300 bg-background-50 text-foreground-800 hover:border-accent-400"
              }`}
            >
              <i className={savedOnly ? "ri-bookmark-fill" : "ri-bookmark-line"}></i>
              只看收藏（{saved.length}）
            </button>
          </div>

          <div className="mt-6 flex flex-wrap gap-2">
            {categories.map((cat) => (
              <button
                key={cat.key}
                type="button"
                onClick={() => setCategory(cat.key)}
                className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-sm whitespace-nowrap transition-colors cursor-pointer ${
                  category === cat.key
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-100 text-foreground-700 hover:bg-background-200"
                }`}
              >
                <i className={cat.icon}></i>
                {cat.key}
              </button>
            ))}
          </div>

          <p className="mt-8 text-sm text-foreground-500">
            共找到 <span className="text-primary-700 font-medium">{filtered.length}</span> 条话题
          </p>

          {shown.length > 0 ? (
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {shown.map((topic) => (
                <TopicCard key={topic.id} topic={topic} />
              ))}
            </div>
          ) : (
            <div className="mt-6 flex flex-col items-center justify-center gap-3 py-20 rounded-lg border border-dashed border-background-300 bg-background-50 text-center">
              <span className="w-12 h-12 flex items-center justify-center rounded-full bg-background-100 text-foreground-500">
                <i className="ri-inbox-line text-2xl"></i>
              </span>
              <p className="text-sm text-foreground-600">没有匹配的话题，换个关键词或分类试试。</p>
              <button
                type="button"
                onClick={() => {
                  setQuery("");
                  setCategory("全部");
                  setSavedOnly(false);
                }}
                className="text-sm text-primary-700 hover:text-primary-800 cursor-pointer"
              >
                重置筛选条件
              </button>
            </div>
          )}

          {visible < filtered.length && (
            <div className="mt-10 flex justify-center">
              <button
                type="button"
                onClick={() => setVisible((v) => v + PAGE_SIZE)}
                className="px-6 py-3 rounded-md border border-foreground-300 text-sm font-medium text-foreground-800 hover:border-primary-400 hover:text-primary-700 transition-colors cursor-pointer"
              >
                加载更多（剩余 {filtered.length - visible} 条）
              </button>
            </div>
          )}
        </div>
      </main>
      <SiteFooter />
      <BackToTop />
      <JsonLd id="topics-breadcrumb" data={breadcrumbLd} />
      <JsonLd id="topics-collection" data={collectionLd} />
    </div>
  );
}